import { fetchConfig } from '@theme/utilities';
import { CartLinesUpdateEvent } from '@shopify/events';

/**
 * Wraps a plain cart-add <form> so it adds by AJAX instead of navigating to /cart.
 * It sends the same CartLinesUpdateEvent the theme's product form does, so the cart
 * drawer re-renders and auto-opens (settings.auto_open_cart_drawer).
 *
 * <vf-cart-add><form action="/cart/add">…id, quantity (or items[n][id], items[n][quantity])…</form></vf-cart-add>
 */
class VfCartAdd extends HTMLElement {
  connectedCallback() {
    this.form = this.querySelector('form');
    this.form?.addEventListener('submit', this.#submit);
  }

  disconnectedCallback() {
    this.form?.removeEventListener('submit', this.#submit);
  }

  #submit = async (event) => {
    event.preventDefault();
    const button = this.form.querySelector('[type="submit"]');
    if (button?.disabled) return;
    if (button) button.disabled = true;

    const formData = new FormData(this.form);
    const sectionIds = [...document.querySelectorAll('cart-items-component')]
      .map((el) => el instanceof HTMLElement && el.dataset.sectionId)
      .filter(Boolean);
    if (sectionIds.length) formData.append('sections', sectionIds.join(','));

    // One item (id, quantity) or several (items[n][id], items[n][quantity]).
    const lines = [];
    for (const [key, value] of formData) {
      const match = key.match(/^items\[(\d+)\]\[id\]$/);
      if (match) {
        lines.push({ merchandiseId: String(value), quantity: Number(formData.get(`items[${match[1]}][quantity]`)) || 1 });
      }
    }
    if (!lines.length) {
      lines.push({ merchandiseId: String(formData.get('id')), quantity: Number(formData.get('quantity')) || 1 });
    }

    const deferred = CartLinesUpdateEvent.createPromise();
    this.dispatchEvent(new CartLinesUpdateEvent({ action: 'add', context: 'product', lines, promise: deferred.promise }));

    try {
      const config = fetchConfig('javascript', { body: formData });
      const response = await fetch(Theme.routes.cart_add_url, {
        ...config,
        headers: { ...config.headers, Accept: 'text/html' },
      }).then((res) => res.json());
      const ajaxCart = await fetch(`${Theme.routes.cart_url}.js`).then((res) => res.json());
      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(ajaxCart),
        detail: {
          items: ajaxCart.items,
          source: 'vf-cart-add',
          sections: response.sections,
          didError: Boolean(response.status),
        },
      });
    } catch (error) {
      console.error(error);
      deferred.reject(error);
    } finally {
      if (button) button.disabled = false;
    }
  };
}

if (!customElements.get('vf-cart-add')) customElements.define('vf-cart-add', VfCartAdd);
